/**
 * Helpers partagés — tuile portail « Entreprise » (Batch #14.243).
 */
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { API_BASE } from "../helpers.js";
import { getPortalToken } from "@/lib/portalSession";
import { fmtCAD, today, r2 } from "@/lib/format";
export { fmtCAD, today, r2 };

export const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";

/** Taxes à partir d'un montant AVANT taxes (facture émise par le client). */
export const taxesFromSubtotal = (subtotal, tpsRate, tvqRate) => {
  const s = Number(subtotal) || 0;
  return { tps: r2(s * tpsRate), tvq: r2(s * tvqRate) };
};

/** Taxes extraites d'un montant TOTAL payé (reçu de dépense, taxes incluses). */
export const taxesFromTotal = (total, tpsRate, tvqRate) => {
  const t = Number(total) || 0;
  const s = t / (1 + tpsRate + tvqRate);
  const tps = r2(s * tpsRate);
  return { tps, tvq: r2(t - s - tps) };
};

/** Retour d'écriture : vrai si la démo a simulé l'action (aucune donnée enregistrée). */
export const isDemoResponse = (res) => {
  if (res?.data?.demo_mode) {
    toast.message("Mode démo : action simulée, rien n'est enregistré.");
    return true;
  }
  return false;
};

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

export function SectionCard({ title, icon: Icon, primaryColor, action, children, testId }) {
  return (
    <div className="bg-white border rounded-2xl p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-900">
          {Icon && <Icon className="w-4 h-4" style={{ color: primaryColor }} />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/** Bouton d'ajout d'une section : pleine largeur sur mobile. */
export const addBtnCls = "w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white text-sm font-semibold";
/** Barre d'actions d'un formulaire : Annuler / Enregistrer empilés sur mobile. */
export const formActionsCls = "flex flex-col-reverse sm:flex-row gap-2 sm:justify-end";
export const cancelBtnCls = "w-full sm:w-auto px-4 py-2.5 rounded-lg border text-sm text-gray-600 bg-white";
export const saveBtnCls = "w-full sm:w-auto px-5 py-2.5 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50";
/** Bouton icône supprimer (cible tactile ≥ 40px). */
export const deleteBtnCls = "p-2.5 -mr-2 text-gray-300 hover:text-red-600 tap-target flex items-center justify-center";

export function TaxToggle({ checked, onChange, label, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-blue-600" data-testid={testId} />
      {label}
    </label>
  );
}

/** Miniature de reçu protégée — chargée avec le jeton portail. */
export function PortalReceiptThumb({ url, className = "w-12 h-12" }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!url) return undefined;
    let blobUrl = "";
    fetch(url.startsWith("http") ? url : `${API_BASE}${url}`, { headers: { Authorization: `Bearer ${getPortalToken()}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b) { blobUrl = URL.createObjectURL(b); setSrc(blobUrl); } })
      .catch(() => {});
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);
  if (!url) return null;
  const isPdf = url.toLowerCase().endsWith(".pdf");
  return (
    <a href={src || "#"} target="_blank" rel="noopener noreferrer" title="Voir le reçu"
      className={`${className} rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {isPdf || !src ? <span className="text-[9px] text-gray-500 font-semibold">{isPdf ? "PDF" : "…"}</span>
        : <img src={src} alt="Reçu" className="w-full h-full object-cover" />}
    </a>
  );
}
